<template>
  <div class="single-date-picker">
    <div
      class="date-display"
      :class="{ 'clickable': true }"
      @click="toggleDatePicker"
    >
      <slot :formattedDate="formatDate(modelValue)">
        {{ formatDate(modelValue) }}
      </slot>
    </div>
    <div v-if="showDatePicker" class="date-picker-dropdown" :class="`align-${alignment}`">
      <div class="date-picker-header">
        <button @click="previousMonth" class="month-nav-btn">‹</button>
        <span class="current-month">{{ currentMonthYear }}</span>
        <button @click="nextMonth" class="month-nav-btn">›</button>
      </div>
      <div class="calendar-grid">
        <div class="weekday-header" v-for="day in weekDays" :key="day">{{ day }}</div>
        <div
          v-for="date in calendarDates"
          :key="date.key"
          class="calendar-date"
          :class="{
            'other-month': !date.isCurrentMonth,
            'selected': isDateSelected(date.date),
            'disabled': isDateDisabled(date.date)
          }"
          @click="selectDate(date)"
        >
          {{ date.day }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { colors } from '../../../../consts/colors';

const { t: $t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Date,
    default: () => new Date()
  },
  alignment: {
    type: String,
    default: 'center',
    validator: (value) => ['left', 'center', 'right'].includes(value)
  },
  minDate: {
    type: Date,
    default: null
  },
  maxDate: {
    type: Date,
    default: null
  },
  dateFormat: {
    type: String,
    default: 'YYYY/MM/DD'
  }
});

const emit = defineEmits(['update:modelValue']);

const showDatePicker = ref(false);
const currentMonth = ref(new Date(props.modelValue)); // Initialize to selected date's month

// Watch for modelValue changes to update currentMonth
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    currentMonth.value = new Date(newValue);
  }
});

// Weekdays
const weekDays = computed(() => [
  $t('common.calendar.weekdays.sun'),
  $t('common.calendar.weekdays.mon'),
  $t('common.calendar.weekdays.tue'),
  $t('common.calendar.weekdays.wed'),
  $t('common.calendar.weekdays.thu'),
  $t('common.calendar.weekdays.fri'),
  $t('common.calendar.weekdays.sat'),
]);

// Format date for display
const formatDate = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // Replace format tokens
  return props.dateFormat
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day);
};

// Current month/year display
const currentMonthYear = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = String(currentMonth.value.getMonth() + 1).padStart(2, '0');
  return `${year}/${month}`;
});

// Generate calendar dates
const calendarDates = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDayOfWeek = firstDay.getDay();
  const dates = [];

  // Previous month dates
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const day = prevMonthLastDay - i;
    dates.push({
      day,
      date: new Date(year, month - 1, day),
      isCurrentMonth: false,
      key: `prev-${day}`
    });
  }

  // Current month dates
  for (let day = 1; day <= lastDay.getDate(); day++) {
    dates.push({
      day,
      date: new Date(year, month, day),
      isCurrentMonth: true,
      key: `current-${day}`
    });
  }

  // Next month dates
  const remainingDays = 42 - dates.length; // 6 weeks * 7 days
  for (let day = 1; day <= remainingDays; day++) {
    dates.push({
      day,
      date: new Date(year, month + 1, day),
      isCurrentMonth: false,
      key: `next-${day}`
    });
  }

  return dates;
});

// Date picker methods
const toggleDatePicker = () => {
  showDatePicker.value = !showDatePicker.value;
};

const previousMonth = () => {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() - 1,
    1
  );
};

const nextMonth = () => {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() + 1,
    1
  );
};

const selectDate = (dateObj) => {
  if (!dateObj.isCurrentMonth || isDateDisabled(dateObj.date)) return;

  // Emit the selected date immediately
  emit('update:modelValue', dateObj.date);

  // Close the date picker
  showDatePicker.value = false;
};

const isDateSelected = (date) => {
  if (!props.modelValue) return false;
  const dateStr = date.toDateString();
  const selectedStr = props.modelValue.toDateString();
  return dateStr === selectedStr;
};

const isDateDisabled = (date) => {
  // Normalize date to start of day for accurate comparison
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  // Check if date is before minDate
  if (props.minDate) {
    const minDateNormalized = new Date(props.minDate);
    minDateNormalized.setHours(0, 0, 0, 0);
    if (normalizedDate < minDateNormalized) {
      return true;
    }
  }

  // Check if date is after maxDate
  if (props.maxDate) {
    const maxDateNormalized = new Date(props.maxDate);
    maxDateNormalized.setHours(0, 0, 0, 0);
    if (normalizedDate > maxDateNormalized) {
      return true;
    }
  }

  // If no min/max date is provided, fallback to default behavior
  // Allow dates up to the end of 2 years from now
  if (!props.minDate && !props.maxDate) {
    const today = new Date();
    const maxYear = today.getFullYear() + 2;
    const defaultMaxDate = new Date(maxYear, 11, 31); // December 31st of 2 years from now
    defaultMaxDate.setHours(23, 59, 59, 999);
    return date > defaultMaxDate;
  }

  return false;
};

// Close date picker when clicking outside
const handleClickOutside = (event) => {
  const datePicker = event.target.closest('.single-date-picker');
  if (!datePicker && showDatePicker.value) {
    showDatePicker.value = false;
  }
};

// Add event listener when mounted
if (typeof window !== 'undefined') {
  document.addEventListener('click', handleClickOutside);
}

// Clean up event listener
import { onBeforeUnmount } from 'vue';
onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    document.removeEventListener('click', handleClickOutside);
  }
});
</script>

<style lang="scss" scoped>
.single-date-picker {
  position: relative;
  display: inline-block;

  .date-display {
    &.clickable {
      cursor: pointer;
    }
  }

  .date-picker-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    background: v-bind('colors.white');
    border: 1px solid v-bind('colors.gray[200]');
    border-radius: 8px;
    box-shadow: 0 4px 12px v-bind('colors.shadowMedium');
    padding: 16px;
    z-index: 10000;
    min-width: 320px;

    &.align-left {
      right: auto;
      left: 0;
    }

    &.align-center {
      left: 50%;
      transform: translateX(-50%);
    }

    &.align-right {
      left: auto;
      right: 0;
    }
  }

  .date-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .month-nav-btn {
      background: none;
      border: none;
      font-size: 20px;
      color: v-bind('colors.gray[500]');
      cursor: pointer;
      padding: 4px 12px;

      &:hover {
        color: v-bind('colors.textPrimary');
      }
    }

    .current-month {
      font-size: 16px;
      font-weight: 600;
      color: v-bind('colors.textPrimary');
    }
  }

  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;

    .weekday-header {
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      color: v-bind('colors.gray[500]');
      padding: 8px 4px;
    }

    .calendar-date {
      text-align: center;
      padding: 8px 4px;
      font-size: 14px;
      color: v-bind('colors.textPrimary');
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s;

      &:hover:not(.disabled):not(.other-month) {
        background-color: v-bind('colors.gray[300]');
      }

      &.other-month {
        color: v-bind('colors.disabled');
        cursor: default;
      }

      &.selected {
        background-color: v-bind('colors.blue[500]');
        color: v-bind('colors.white');
        font-weight: 600;
      }

      &.disabled {
        color: v-bind('colors.disabled');
        cursor: not-allowed;
      }
    }
  }
}
</style>
